import React, {
  Component, Fragment, lazy, Suspense,
} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import StarRatings from 'react-star-ratings';
import MoreActions from './MoreActions';
import UserViewsTable from '../../UserViewsTable';
import ProposalResponse from '../../ProposalResponse';
import ProfileCompletion from '../ProfileCompletion';
import ProposalFeedbacks from '../../ProposalFeedbacks';
import AddProposalPastRoles from '../../AddProposalPastRoles';
import userMapIcon from '../../../../assets/images/icons/map-pin.png';
import cloraIcon from '../../../../assets/images/onboarding-state-active.svg';
import profileIcon from '../../../../assets/images/profile-user.png';
import experienceIcon from '../../../../assets/images/icons/experience.svg';
import clockIcon from '../../../../assets/images/icons/clock-yellow.svg';
import checkmarkImg from '../../../../assets/images/check-mark.svg';
import remarkStarsIcon from '../../../../assets/images/remark_stars.svg';
import Loading from '../Loading';
import capitalize from '../../../stringUtils';
import { postRequest, putRequest } from '../../../requestUtils';
import './ProposalShow.scss';

const AcceptProposal = lazy(() => import('../../AcceptProposal'));
const ConfirmSubmitProposal = lazy(() => import('../ConfirmSubmitProposal'));
const ScheduleInterviewModal = lazy(() => import('../../ScheduleInterviewModal'));
const SubmitToEmployer = lazy(() => import('../../SubmitToEmployer'));
const AskQuestion = lazy(() => import('../../AskQuestion'));
const ProposalQuestion = lazy(() => import('../ProposalQuestion'));
const ConfirmModal = lazy(() => import('../../ConfirmModal'));
const DeclineProposal = lazy(() => import('../../DeclineProposal'));
const AddCoworkersModal = lazy(() => import('../../AddCoworkersModal'));
const ShareResource = lazy(() => import('../../ShareResource'));
const ProposalInterviews = lazy(() => import('../../ProposalInterviews'));
const WaitlistedNotification = lazy(() => import('../WaitlistedNotification'));
const Resume = lazy(() => import('../Resume'));
const InterviewQuestions = lazy(() => import('../../InterviewQuestions'));
const WithdrawProposal = lazy(() => import('../WithdrawProposal'));
const TransferApplication = lazy(() => import('../TransferApplication'));

const tabs = (document = null, isCloraRep, showInterviewTab) => {
  const tabsArray = ['Proposal'];
  if (isCloraRep && showInterviewTab) tabsArray.push('Interview Questions');
  if (document) tabsArray.push('Resume');
  return tabsArray;
};

const renderCheckmark = (showImage) => (showImage
  ? <img src={checkmarkImg} className="checkmark-img" alt="check mark" /> : null);

const imgTag = (icon, altText) => (
  <img className="me-2" src={icon} alt={`${altText}`} />
);

const handlePrint = () => {
  const tempTitle = document.title;

  const project = $('#print-proposal').data('project');
  const consultant = $('#print-proposal').data('consultant');

  document.title = `Clora Proposal - ${project} - ${consultant}`;
  window.print();
  document.title = tempTitle;
};

const renderViewOpportunities = (btnClass) => (
  <a
    href="/opportunities"
    className={`btn ${btnClass}`}
  >
    View Other Opportunities
  </a>
);

const renderResponse = ({
  checked, description, notes, extraClasses, extraSpanClasses,
}) => (
  <ProposalResponse
    checked={checked}
    description={description}
    notes={notes}
    extraClasses={extraClasses}
    extraSpanClasses={extraSpanClasses}
  />
);

const renderPastRoles = (pastRoles) => (
  <Fragment>
    <h2>
      Relevant Past Roles
      <span
        type="button"
        className="ms-2"
        data-toggle="tooltip"
        data-placement="bottom"
        title="Relevant past roles are roles the candidate has held in the past that are related to this project."
      >
        <i className="fas fa-info-circle" />
      </span>
    </h2>
    {pastRoles.map((pr) => (
      <div className="body-text-2 fs-hide past-role mb-3">
        <div className="medium-weight">{pr.title}</div>
        <div>{pr.role}</div>
        <div>{pr.company_name}</div>
        <div className="mute fs-hide">{pr.experience}</div>
      </div>
    ))}
  </Fragment>
);

const renderAdvisorRemark = (advisorRemark) => (
  <div className="clora-advisor-remark">
    <div>
      <h2 className="mt-0 mb-0">Clora Remark</h2>
      {imgTag(remarkStarsIcon, 'Stars')}
    </div>
    <div>
      {advisorRemark}
    </div>
  </div>
);

const renderExpertiseTag = (title, expertiseTag, extraClasses = null) => (
  <div className={`col-xs-12 ${extraClasses}`}>
    <h2 className="hearder-teal-green">{title}</h2>
    {expertiseTag.tags.map((ta) => (
      <ProposalResponse
        checked={ta.checked}
        description={ta.description}
      />
    ))}
    {expertiseTag.comments
    && (
    <ProposalResponse
      description={expertiseTag.comments.description}
      notes={expertiseTag.comments.notes}
    />
    )}
  </div>
);

class ProposalShow extends Component {
  constructor(props) {
    super(props);
    const { proposalDetails } = props;
    this.state = {
      selectedTab: 'Proposal',
      document: proposalDetails.document,
      originalDocument: proposalDetails.originalDocument,
      headerTabs: tabs(proposalDetails.document, proposalDetails.clora_rep, proposalDetails.show_interview_tab),
      proposalDetails,
      project: null,
      isSticky: false,
      showAddCoworkerModal: false,
      showAskQuestionModal: false,
      showProposalFeedbackModal: false,
      showWaitlistModal: false,
      errorMessage: '',
    };
    this.clickTab = this.clickTab.bind(this);
    this.renderTab = this.renderTab.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.renderSendEmail = this.renderSendEmail.bind(this);
    this.renderHireAgain = this.renderHireAgain.bind(this);
    this.renderPrintToPDF = this.renderPrintToPDF.bind(this);
    this.renderTabContent = this.renderTabContent.bind(this);
    this.handleAfterSubmit = this.handleAfterSubmit.bind(this);
    this.renderConfirmModal = this.renderConfirmModal.bind(this);
    this.renderMoreActions = this.renderMoreActions.bind(this);
    this.renderAskQuestion = this.renderAskQuestion.bind(this);
    this.renderSendContract = this.renderSendContract.bind(this);
    this.renderEditProposal = this.renderEditProposal.bind(this);
    this.renderNameAndStatus = this.renderNameAndStatus.bind(this);
    this.renderHeaderActions = this.renderHeaderActions.bind(this);
    this.renderLeaveFeedback = this.renderLeaveFeedback.bind(this);
    this.renderShareResource = this.renderShareResource.bind(this);
    this.renderAcceptProposal = this.renderAcceptProposal.bind(this);
    this.renderDeleteProposal = this.renderDeleteProposal.bind(this);
    this.renderSubmitProposal = this.renderSubmitProposal.bind(this);
    this.handleShowAskQuestion = this.handleShowAskQuestion.bind(this);
    this.renderDeclineProposal = this.renderDeclineProposal.bind(this);
    this.renderleftBodyContent = this.renderleftBodyContent.bind(this);
    this.renderWathdrawProposal = this.renderWathdrawProposal.bind(this);
    this.handleWaitlistProposal = this.handleWaitlistProposal.bind(this);
    this.renderWaitlistProposal = this.renderWaitlistProposal.bind(this);
    this.renderSubmitToEmployer = this.renderSubmitToEmployer.bind(this);
    this.renderForceStatusChange = this.renderForceStatusChange.bind(this);
    this.handleShowCoworkerModal = this.handleShowCoworkerModal.bind(this);
    this.renderShareWithColleague = this.renderShareWithColleague.bind(this);
    this.handleOptionClick = debounce(this.handleOptionClick.bind(this), 300);
    this.renderTransferApplication = this.renderTransferApplication.bind(this);
    this.handleToggleWaitlistModal = this.handleToggleWaitlistModal.bind(this);
    this.handleShowProposalFeedback = this.handleShowProposalFeedback.bind(this);
    this.renderScheduleInterviewModal = this.renderScheduleInterviewModal.bind(this);
    this.handleClearErrorMessage = this.handleClearErrorMessage.bind(this);
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  getButtonActions() {
    const { createInterviewUrl } = this.props;
    const { proposalDetails } = this.state;
    const { action_buttons: actionButtons, employer } = proposalDetails;

    const actions = {
      'Submit Proposal': (buttonStyles) => this.renderSubmitProposal(buttonStyles.substring(4)),
      Decline:
        (buttonStyles) => this.renderDeclineProposal('decline', 'Decline', 'button', buttonStyles),
      Withdraw: (buttonStyles) => this.renderWathdrawProposal('button', buttonStyles),
      'Request Edit': (buttonStyles) => this.renderAskQuestion(buttonStyles),
      'Proposal Question': (buttonStyles) => this.renderProposalQuestion('button', buttonStyles),
      'Leave Feedback': (buttonStyles) => this.renderLeaveFeedback('Edit Reply', false, buttonStyles),
      'Submit to Employer': (buttonStyles) => this.renderSubmitToEmployer('button', buttonStyles.substring(4)),
      'Schedule Another Interview':
        (buttonStyles) => this.renderScheduleInterviewModal(`${employer ? 'Request' : 'Schedule '} Another Interview`, 'button', buttonStyles),
      Waitlist:
        (buttonStyles) => this.renderWaitlistProposal('button', buttonStyles),
      'Schedule Interview':
        (buttonStyles) => this.renderConfirmModal('interview', 'Schedule Interview', createInterviewUrl, 'button', buttonStyles),
      'Request Interview':
        (buttonStyles) => this.renderScheduleInterviewModal('Request Interview', 'button', buttonStyles),
      'Share with a Colleague': (buttonStyles) => this.renderShareWithColleague('button', buttonStyles),
      Hire: (buttonStyles) => this.renderAcceptProposal('hire', `Hire ${proposalDetails.consultant_first_name}`, 'button', buttonStyles),
      'Hire Again':
        (buttonStyles) => this.renderHireAgain(buttonStyles),
      'Send Contract': (buttonStyles) => this.renderSendContract(buttonStyles),
      'Send Email to Consultant': (buttonStyles) => this.renderSendEmail(buttonStyles),
      'View Other Opportunities': (buttonStyles) => renderViewOpportunities(buttonStyles),
      'Complete Proposal': (buttonStyles) => this.renderEditProposal('Complete Proposal', buttonStyles),
      Edit: (buttonStyles) => this.renderEditProposal('Edit', buttonStyles),
      'More Actions': (buttonStyles) => this.renderMoreActions(buttonStyles),
    };

    const skipButtons = ['Decline', 'Share with a Colleague', 'View Other Opportunities'];
    const linkButton = ['Ask Question', 'Leave Feedback', 'Proposal Question', 'Withdraw'];
    let firstButtonSet = false;

    const buttonActions = actionButtons
      .map((action) => {
        if (action === 'More Actions') {
          return actions[action]?.();
        }

        let buttonStyles = 'btn--border-darkest';

        if (linkButton.includes(action)) {
          buttonStyles = 'btn--link-blue';
        } else if (!firstButtonSet && !skipButtons.includes(action)) {
          buttonStyles = 'btn--green-darkest';
          firstButtonSet = true;
        }

        return actions[action]?.(buttonStyles);
      }).filter(Boolean);
    return buttonActions;
  }

  getDropdownList() {
    const {
      createInterviewUrl,
      consultantDetails,
    } = this.props;
    const { proposalDetails } = this.state;
    const { more_actions: moreActions, employer } = proposalDetails;

    const actions = {
      Edit: this.renderEditProposal('Edit'),
      Decline: this.renderDeclineProposal('decline', 'Decline'),
      'Ask Question': this.renderAskQuestion('link'),
      'Send To Employer': this.renderSubmitToEmployer(),
      'Schedule Another Interview':
        this.renderScheduleInterviewModal(`${employer ? 'Request' : 'Schedule '} Another Interview`),
      'Schedule Interview': this.renderConfirmModal('interview', 'Schedule Interview', createInterviewUrl),
      'Share Proposal': this.renderShareResource(),
      'Share with a Colleague': this.renderShareWithColleague('link'),
      Hire: this.renderAcceptProposal('hire', `Hire ${consultantDetails.first_name}`),
      Withdraw: this.renderWathdrawProposal(),
      'Provide feedback': this.renderLeaveFeedback('Provide feedback', true, 'link'),
      'Send Contract': this.renderSendContract(),
      'Send Email to Consultant': this.renderSendEmail(),
      'Move to Another Project': this.renderTransferApplication(),
      'Status Tracking': this.renderStatusTracking(),
      'Force Status Change': this.renderForceStatusChange(),
      'Feedback History': this.renderFeedbackHistory(),
      'Print to PDF': this.renderPrintToPDF(),
      Waitlist: this.renderWaitlistProposal('link'),
      'Proposal Question': this.renderProposalQuestion('link'),
      Delete: this.renderDeleteProposal(),
    };

    const dropdownList = moreActions
      .filter((action) => actions[action])
      .map((action) => actions[action]);

    return dropdownList;
  }

  clickTab(tabText) {
    this.setState({ selectedTab: tabText });
  }

  handleAfterSubmit(data = {}) {
    if (Object.keys(data).length !== 0) {
      this.setState({ proposalDetails: data });
    }
  }

  handleShowAskQuestion() {
    this.setState({
      showAskQuestionModal: true,
      showWaitlistModal: false,
    });
  }

  handleClearErrorMessage() {
    setTimeout(() => {
      this.setState({ errorMessage: '' });
    }, 6000);
  }

  handleShowProposalFeedback() {
    this.setState({ showProposalFeedbackModal: true });
  }

  handleToggleWaitlistModal() {
    this.setState((prevState) => ({
      showWaitlistModal: !prevState.showWaitlistModal,
      showAskQuestionModal: false,
      showAddCoworkerModal: false,
    }));
  }

  handleScroll() {
    this.setState({ isSticky: window.scrollY > 200 });
  }

  handleShowCoworkerModal() {
    this.setState({
      showAddCoworkerModal: true,
      showWaitlistModal: false,
    });
  }

  handleOptionClick(event, value) {
    event.preventDefault();
    const { csrfToken, proposalResourceId, currentUserEmail } = this.props;

    postRequest(
      `/proposals/${proposalResourceId}/proposal_feedbacks/`,
      { proposal_feedback: { consultant_fit: value, email: currentUserEmail } },
      csrfToken,
    ).then(({ data }) => {
      this.handleAfterSubmit(data.proposalDetails);
      this.handleShowProposalFeedback();
    });
  }

  handleWaitlistProposal() {
    const { csrfToken, proposalResourceId } = this.props;
    putRequest(
      `/proposals/${proposalResourceId}/waitlist`,
      {},
      csrfToken,
    ).then(({ data }) => {
      this.handleAfterSubmit(data);
      this.handleToggleWaitlistModal();
    }).catch((e) => {
      this.setState({ errorMessage: e?.base[0] || e?.status[0] });
      this.handleClearErrorMessage();
    });
  }

  renderAskQuestion(buttonStyles = null) {
    const {
      cloraCreaeRroposalCommentsUrl,
      csrfToken,
    } = this.props;
    const { proposalDetails } = this.state;

    return (
      <Suspense fallback={<Loading />}>
        <AskQuestion
          formUrl={cloraCreaeRroposalCommentsUrl}
          csrfToken={csrfToken}
          recipientName={proposalDetails.consultant_name}
          {...(buttonStyles && { buttonStyles })}
          triggerType="link"
        />
      </Suspense>
    );
  }

  renderProposalQuestion(type = 'link', buttonStyles = null) {
    if (type === 'link') {
      return (
        <a
          className="link"
          role="button"
          tabIndex={0}
          onClick={this.handleShowAskQuestion}
        >
          Ask a Question about this candidate
        </a>
      );
    }

    return (
      <button
        className={`btn ${buttonStyles}`}
        onClick={this.handleShowAskQuestion}
      >
        Ask a Question
      </button>
    );
  }

  renderAcceptProposal(type, triggerCopy, triggerType = 'link', buttonStyles = null) {
    const { csrfToken, acceptProposalUrl } = this.props;
    const { proposalDetails } = this.state;

    if (proposalDetails.can_be_accepted) {
      if (proposalDetails.employer) {
        return (
          <Suspense fallback={<Loading />}>
            <AcceptProposal
              formUrl={acceptProposalUrl}
              csrfToken={csrfToken}
              consultantName={proposalDetails.consultant_first_name}
              triggerType={triggerType}
              {...(buttonStyles && { buttonStyles: buttonStyles.substring(4) })}
            />
          </Suspense>
        );
      }
      return this.renderConfirmModal(
        type,
        triggerCopy,
        acceptProposalUrl,
        triggerType,
        buttonStyles,
      );
    }

    return null;
  }

  renderConfirmModal(type, triggerCopy, submitUrl, triggerType = 'link', buttonType = null) {
    const {
      csrfToken,
      invitableRoles,
      visibleFeedbackOptions,
      hiddenFeedbackOptions,
      initialTeamMembers,
      proposalResourceId,
      showNewProposalUX,
      newInterviewSlot,
      availableInterviewSlotUrl,
    } = this.props;
    const { proposalDetails } = this.state;

    const options = ['Decline'].includes(triggerCopy) ? visibleFeedbackOptions : hiddenFeedbackOptions;

    return (
      <Suspense fallback={<Loading />}>
        <ConfirmModal
          submitUrl={submitUrl}
          proposalResourceId={proposalResourceId}
          csrfToken={csrfToken}
          consultantName={proposalDetails.consultant_name}
          possibleInterviewers={initialTeamMembers}
          invitableRoles={invitableRoles}
          modalTriggerType={triggerType}
          type={type}
          reasonOptions={options}
          modalTriggerCopy={triggerCopy}
          handleProposalAfterSubmit={(data) => this.handleAfterSubmit(data)}
          {...(buttonType && { buttonType })}
          showNewProposalUX={showNewProposalUX}
          newInterviewSlot={newInterviewSlot}
          availableInterviewSlotUrl={availableInterviewSlotUrl}
        />
      </Suspense>
    );
  }

  renderDeclineProposal(type, triggerCopy, triggerType = 'link', buttonStyles = null) {
    const {
      csrfToken,
      visibleFeedbackOptions,
      showNewProposalUX,
    } = this.props;
    const { proposalDetails } = this.state;

    if ((proposalDetails.employer && proposalDetails.can_be_rejected)
          || (proposalDetails.rep && proposalDetails.can_be_rejected_by_clora)) {
      return (
        <Suspense fallback={<Loading />}>
          <DeclineProposal
            formUrl={proposalDetails.reject_proposal_url}
            csrfToken={csrfToken}
            consultantName={proposalDetails.consultant_name}
            triggerType={triggerType}
            userType={proposalDetails.rep ? 'clora_rep' : 'employer'}
            feedbackOptions={visibleFeedbackOptions}
            handleProposalAfterSubmit={(data) => this.handleAfterSubmit(data)}
            {...(buttonStyles && { buttonStyles: buttonStyles.substring(4) })}
            showNewProposalUX={showNewProposalUX}
          />
        </Suspense>
      );
    }

    return this.renderConfirmModal(
      type,
      triggerCopy,
      proposalDetails.reject_proposal_url,
      triggerType,
      buttonStyles,
    );
  }

  renderLeaveFeedback(buttonText, showLinkOnly, buttonStyles) {
    const { proposalDetails } = this.state;

    const { feedback } = proposalDetails;
    return (
      <Fragment>
        { showLinkOnly
          ? (
            <button
              type="button"
              onClick={() => { this.setState({ showProposalFeedbackModal: true }); }}
              className={buttonStyles}
            >
              {buttonText}
            </button>
          )
          : (
            <div className="leave-feedback">
              <span className="feedback-question">
                Is
                {' '}
                {proposalDetails.consultant_first_name}
                {' '}
                a good fit for this project?
              </span>
              <div className="button-group">
                {feedback.consultant_fit
                  ? (
                    <Fragment>
                      <span className={`status ${feedback.consultant_fit}`}>
                        {feedback.consultant_fit ? feedback.consultant_fit.toUpperCase() : ''}
                      </span>
                      <button
                        type="button"
                        onClick={this.handleShowProposalFeedback}
                        className={buttonStyles}
                      >
                        {buttonText}
                      </button>
                    </Fragment>
                  )
                  : (
                    <Fragment>
                      <button
                        className={`option-button ${feedback.consultant_fit === 'yes' ? 'selected' : ''}`}
                        onClick={(event) => this.handleOptionClick(event, 'yes')}
                      >
                        {renderCheckmark(feedback.consultant_fit === 'yes')}
                        Yes
                      </button>
                      <button
                        className={`option-button ${feedback.consultant_fit === 'maybe' ? 'selected' : ''}`}
                        onClick={(event) => this.handleOptionClick(event, 'maybe')}
                      >
                        {renderCheckmark(feedback.consultant_fit === 'maybe')}
                        Maybe
                      </button>
                      <button
                        className={`option-button ${feedback.consultant_fit === 'no' ? 'selected' : ''}`}
                        onClick={(event) => this.handleOptionClick(event, 'no')}
                      >
                        {renderCheckmark(feedback.consultant_fit === 'no')}
                        No
                      </button>
                    </Fragment>
                  )}
              </div>
            </div>
          )}
      </Fragment>
    );
  }

  renderShareWithColleague(type = 'link', buttonStyles = null) {
    if (type === 'link') {
      return (
        <a
          className="link"
          role="button"
          tabIndex={0}
          onClick={this.handleShowCoworkerModal}
        >
          Share with a Colleague
        </a>
      );
    }

    return (
      <button
        className={`btn ${buttonStyles}`}
        onClick={this.handleShowCoworkerModal}
      >
        Share with a Colleague
      </button>
    );
  }

  renderWaitlistProposal(type = 'link', buttonStyles = null) {
    if (type === 'link') {
      return (
        <a
          className="link"
          role="button"
          tabIndex={0}
          onClick={this.handleWaitlistProposal}
        >
          Waitlist this candidate
        </a>
      );
    }

    return (
      <button
        className={`btn ${buttonStyles}`}
        onClick={this.handleWaitlistProposal}
      >
        Waitlist
      </button>
    );
  }

  renderWathdrawProposal(triggerType = 'link', buttonStyles = 'link') {
    const {
      csrfToken,
      consultantFeedbackOptions,
      showNewProposalUX,
      withdrawUrl,
    } = this.props;
    const { proposalDetails } = this.state;

    if (!proposalDetails.employer && proposalDetails.can_be_withdrawn) {
      return (
        <Suspense fallback={<Loading />}>
          <WithdrawProposal
            formUrl={withdrawUrl}
            csrfToken={csrfToken}
            consultantName={proposalDetails.consultant_name}
            triggerType={triggerType}
            userType={proposalDetails.rep ? 'clora_rep' : 'consultant'}
            feedbackOptions={consultantFeedbackOptions}
            handleProposalAfterSubmit={(data) => this.handleAfterSubmit(data)}
            {...(buttonStyles && { buttonStyles: buttonStyles.substring(4) })}
            showNewProposalUX={showNewProposalUX}
          />
        </Suspense>
      );
    }

    return null;
  }

  renderTransferApplication() {
    const {
      projectsOptions, projectResourceId, csrfToken, proposalResourceId,
    } = this.props;

    const applicationProject = projectsOptions.find(
      (project) => project.value === projectResourceId,
    );
    const filteredProjects = projectsOptions.filter(
      (project) => project.value !== projectResourceId,
    );

    return (
      <Suspense fallback={<Loading />}>
        <TransferApplication
          triggerText="Move to another project"
          triggerType="link"
          idKey="move-application-modal"
          selectOptions={filteredProjects}
          csrfToken={csrfToken}
          proposaltResourceId={proposalResourceId}
          applicationProject={applicationProject}
        />
      </Suspense>
    );
  }

  renderSubmitToEmployer(triggerTpy = 'link', buttonStyles = null) {
    const { csrfToken, showNewProposalUX } = this.props;
    const { proposalDetails } = this.state;

    if (proposalDetails.can_be_submitted_to_employer
      && !proposalDetails.missing_required_response) {
      return (
        <Suspense fallback={<Loading />}>
          <SubmitToEmployer
            formUrl={proposalDetails.submit_to_employer_url}
            csrfToken={csrfToken}
            triggerType={triggerTpy}
            resubmit={proposalDetails.can_be_resubmitted_to_employer}
            onBehalf={proposalDetails.can_be_submitted_to_clora}
            agreementSent={proposalDetails.agreement_sent}
            advisorRemark={proposalDetails.advisor_remark}
            {...(buttonStyles && { buttonStyles })}
            handleProposalAfterSubmit={(data) => this.handleAfterSubmit(data)}
            showNewProposalUX={showNewProposalUX}
          />
        </Suspense>
      );
    }

    return null;
  }

  renderScheduleInterviewModal(
    triggerCopy = 'Schedule Another Interview',
    triggerType = 'link',
    buttonStyles = null,
  ) {
    const {
      createInterviewUrl,
      proposalResourceId,
      csrfToken,
      initialTeamMembers,
      invitableRoles,
      newInterviewSlot,
      availableInterviewSlotUrl,
    } = this.props;
    const { proposalDetails } = this.state;

    return (
      <Suspense fallback={<Loading />}>
        <ScheduleInterviewModal
          newInterviewSlot={newInterviewSlot}
          createInterviewUrl={createInterviewUrl}
          proposalResourceId={proposalResourceId}
          csrfToken={csrfToken}
          consultantName={proposalDetails.consultant_name}
          possibleInterviewers={initialTeamMembers}
          invitableRoles={invitableRoles}
          modalTriggerType={triggerType}
          modalTriggerCopy={triggerCopy}
          {...(buttonStyles && { modalTriggerClassName: buttonStyles })}
          showNPSOnSubmit={proposalDetails.should_be_shown_nps}
          availableInterviewSlotUrl={availableInterviewSlotUrl}
        />
      </Suspense>
    );
  }

  renderDeleteProposal() {
    const {
      proposalResourceId,
    } = this.props;

    return (
      <a
        data-confirm="Are you sure you want to delete this proposal?"
        rel="nofollow"
        data-method="delete"
        href={`/proposals/${proposalResourceId}`}
        className="link--text-red"
      >
        Delete
      </a>
    );
  }

  renderStatusTracking() {
    const { proposalResourceId } = this.props;

    return (
      <a
        href={`/proposals/${proposalResourceId}/status_tracking`}
        className="link"
      >
        Status Tracking
      </a>
    );
  }

  renderForceStatusChange() {
    const { proposalResourceId } = this.props;

    return (
      <a
        href={`/proposals/status_change?resource_id=${proposalResourceId}`}
        className="link"
      >
        Force Status Change
      </a>
    );
  }

  renderSendContract() {
    const { proposalResourceId } = this.props;

    return (
      <a
        data-confirm="Are you sure you want to send contract to the consultant?'"
        rel="nofollow"
        data-method="post"
        href={`/proposals/${proposalResourceId}/send_contract`}
        className="link"
      >
        Send Contract
      </a>
    );
  }

  renderSendEmail() {
    const { proposalResourceId } = this.props;

    return (
      <a
        data-confirm="Are you sure you want to send an email to the consultant?"
        rel="nofollow"
        data-method="post"
        href={`/proposals/${proposalResourceId}/send_email`}
      >
        Send Email to Consultant
      </a>
    );
  }

  renderFeedbackHistory() {
    const { proposalResourceId } = this.props;

    return (
      <a
        href={`/proposals/${proposalResourceId}/feedback_history`}
        className="link"
      >
        Feedback History
      </a>
    );
  }

  renderShareResource() {
    const {
      csrfToken,
      sourceOptions,
      showShareAction,
      recipientOptions,
      proposalResourceId,
      generateShareableLink,
    } = this.props;

    if (showShareAction) {
      return (
        <Suspense fallback={<Loading />}>
          <ShareResource
            generateUrl={generateShareableLink}
            csrfToken={csrfToken}
            resourceId={proposalResourceId}
            triggerText="Share Proposal"
            idKey="share-proposal-modal"
            resourceType="Proposal"
            sourceOptions={sourceOptions}
            recipientOptions={recipientOptions}
          />
        </Suspense>
      );
    }

    return null;
  }

  renderPrintToPDF() {
    const { proposalDetails } = this.state;

    return (
      <a
        role="button"
        id="print-proposal"
        className="link"
        onClick={() => handlePrint()}
        data-project={proposalDetails.project_name}
        tabIndex={0}
        data-consultant={proposalDetails.consultant_name}
      >
        Print to PDF
      </a>
    );
  }

  renderNameAndStatus() {
    const { proposalDetails } = this.state;

    return (
      <Fragment>
        <h1 className="consultant-name">{proposalDetails.consultant_name}</h1>
        <span className={`proposal-status ${proposalDetails.status}`}>
          {capitalize(proposalDetails.status)}
        </span>
      </Fragment>
    );
  }

  renderMoreActions() {
    return (
      <MoreActions
        listItems={this.getDropdownList()}
      />
    );
  }

  renderEditProposal(buttonText, btnClass = 'link') {
    const { editProposalUrl } = this.props;

    return (
      <a
        href={editProposalUrl}
        className={`${btnClass === 'link' ? 'link' : `btn ${btnClass}`}`}
      >
        {buttonText}
      </a>
    );
  }

  renderHireAgain(btnClass = 'link') {
    const { proposalDetails } = this.props;

    return (
      <Fragment>
        { proposalDetails.show_hire_again
          && (
          <a
            href={proposalDetails.rehire_url}
            className={`${btnClass === 'link' ? 'link' : `btn ${btnClass}`}`}
          >
            Hire Again
          </a>
          )}
      </Fragment>
    );
  }

  renderHeaderActions(sticky = false) {
    const { errorMessage } = this.state;

    if (sticky) {
      return (
        <div>
          { errorMessage && <div className="error-message">{errorMessage}</div>}
          <div className="header-actions">
            {this.getButtonActions().reverse().map((item) => item)}
          </div>
        </div>
      );
    }
    return (
      <Fragment>
        { errorMessage && <div className="error-message">{errorMessage}</div> }
        <div className="header-actions">
          {this.getButtonActions().map((item) => item)}
        </div>
      </Fragment>
    );
  }

  renderSubmitProposal(buttonStyles) {
    const {
      editProposalUrl,
      animation,
      csrfToken,
      updateUserSettingsUrl,
      disabledSubmitProposal,
      referralProps,
      showNewProposalUX,
    } = this.props;
    const { proposalDetails } = this.state;

    return (
      <Suspense fallback={<Loading />}>
        <ConfirmSubmitProposal
          editUrl={editProposalUrl}
          fullName={proposalDetails.consultant_full_name}
          updateUserSettingsUrl={updateUserSettingsUrl}
          formUrl={proposalDetails.submit_to_employer_url}
          submitToOpenMarketplace={proposalDetails.submit_directly_to_employer}
          csrfToken={csrfToken}
          animation={animation}
          disabled={disabledSubmitProposal}
          referralProps={referralProps}
          onSubmitCallback={() => {}}
          buttonStyles={buttonStyles}
          handleProposalAfterSubmit={(data) => this.handleAfterSubmit(data)}
          showNewProposalUX={showNewProposalUX}
        />
      </Suspense>
    );
  }

  renderTab(tabText, tabIndex) {
    const { selectedTab } = this.state;
    const isSelected = selectedTab === tabText;

    return (
      <a
        role="button"
        className={`proposal-header__tab ${isSelected ? 'tab--active' : ''}`}
        onClick={() => this.clickTab(tabText)}
        key={tabText}
        tabIndex={tabIndex}
      >
        {tabText}
      </a>
    );
  }

  renderleftBodyContent() {
    const {
      proposal,
      proposalPastRolesUrl,
      csrfToken,
      selectedPastRolesIds,
      pastRoles,
      selectedPastRoles,
      proposalQuestionUrl,
      completionPercentage,
      userPath,
      initialTeamMembers,
      invitableRoles,
      consultantInterviewPath,
      canCancelInterview,
      canEditInterview,
      repOrOwner,
      showBackgroundCheck,
      newInterviewSlot,
      availableInterviewSlotUrl,
    } = this.props;
    const {
      proposalDetails,
      showAskQuestionModal,
    } = this.state;

    return (
      <div className="left-body-content">
        { (completionPercentage && completionPercentage < 100)
          && (
          <div className="body-section">
            <ProfileCompletion
              header="Receive better project recommendations, rank higher, and increase your chances of being interviewed."
              percentage={completionPercentage}
              userPath={userPath}
              location={`Proposal Show - ${proposal.status}`}
            />
          </div>
          )}
        <div className="body-section">
          { proposalDetails.hourly_rate
            && (
            <Fragment>
              <h2>{proposalDetails.hourly_rate}</h2>
              <hr />
            </Fragment>
            )}
          {proposalDetails.tax_type && renderResponse(proposalDetails.tax_type)}
          {proposalDetails.work_in_office && renderResponse(proposalDetails.work_in_office)}
          {proposalDetails.availability && renderResponse(proposalDetails.availability)}
          {proposalDetails.us_sponsorship_needed
            && renderResponse(proposalDetails.us_sponsorship_needed)}
          {proposalDetails.agreement && renderResponse(proposalDetails.agreement)}
          {proposalDetails.non_compete && renderResponse(proposalDetails.non_compete)}
          {(showBackgroundCheck && proposalDetails.background_check_type)
            && renderResponse(proposalDetails.background_check_type)}
        </div>
        <div className="body-section">
          {proposalDetails.employer
            ? renderPastRoles(selectedPastRoles)
            : (
              <AddProposalPastRoles
                pastRoles={pastRoles}
                selectedPastRolesIds={selectedPastRolesIds}
                csrfToken={csrfToken}
                proposal={proposal}
                proposalPastRolesUrl={proposalPastRolesUrl}
                isConsultant={proposalDetails.consultant}
                isRep={proposalDetails.rep}
                canEdit
                projectNamn={proposalDetails.project_name}
              />
            )}
        </div>
        { proposalDetails.interviews
          && (
          <div className="body-section">
            <Suspense fallback={<Loading />}>
              <ProposalInterviews
                interviews={proposalDetails.interviews}
                proposalId={proposal.id}
                csrfToken={csrfToken}
                isRep={proposalDetails.rep}
                invitableRoles={invitableRoles}
                possibleInterviewers={initialTeamMembers}
                consultantView={proposalDetails.consultant}
                consultantInterviewPath={consultantInterviewPath}
                canCancel={canCancelInterview}
                canEdit={canEditInterview}
                consultantName={proposalDetails.consultant_name}
                repOrOwner={repOrOwner}
                newInterviewSlot={newInterviewSlot}
                availableInterviewSlotUrl={availableInterviewSlotUrl}
              />
            </Suspense>
          </div>
          )}
        { proposalDetails.employer
          && (
          <div className="body-section">
            <Suspense fallback={<Loading />}>
              <ProposalQuestion
                proposal={proposal}
                csrfToken={csrfToken}
                url={proposalQuestionUrl}
                showModalCallback={() => {
                  this.setState({ showAskQuestionModal: false });
                }}
                modalIsOpen={showAskQuestionModal}
              />
            </Suspense>
          </div>
          )}
      </div>
    );
  }

  renderProposalFeeback() {
    const {
      csrfToken,
      proposalResourceId,
      isSignedIn,
      signIn,
      currentUserEmail,
      showNewProposalUX,
    } = this.props;
    const {
      proposalDetails,
      showProposalFeedbackModal,
    } = this.state;

    return (
      <Fragment>
        { proposalDetails.can_create_feedback
          && (
          <div className="body-section">
            <Suspense fallback={<Loading />}>
              <ProposalFeedbacks
                isSignedIn={isSignedIn}
                signIn={signIn}
                admin={proposalDetails.rep}
                canEditFeedback={proposalDetails.can_edit_feedback}
                ownerName={proposalDetails.ownerName}
                candidateName={proposalDetails.consultant_name}
                proposalResourceId={proposalResourceId}
                id={proposalDetails.feedback.id}
                consultantFit={proposalDetails.feedback.consultant_fit}
                note={proposalDetails.feedback.note}
                csrfToken={csrfToken}
                email={currentUserEmail}
                feedbacks={proposalDetails.feedbacks}
                location="proposal"
                buttonStyles="hearder-teal-green"
                handleShowCoworkerModal={this.handleShowCoworkerModal}
                showModalCallback={() => {
                  this.setState({ showProposalFeedbackModal: false });
                }}
                modalIsOpen={showProposalFeedbackModal}
                handleProposalAfterSubmit={(data) => this.handleAfterSubmit(data)}
                showNewProposalUX={showNewProposalUX}
              />
            </Suspense>
          </div>
          )}
      </Fragment>
    );
  }

  renderTabContent() {
    const {
      csrfToken,
      proposalResourceId,
      proposalViews,
      showAdvisorRemark,
    } = this.props;
    const {
      proposalDetails,
      document,
      originalDocument,
      selectedTab,
    } = this.state;

    switch (selectedTab) {
      case 'Resume':
        return (
          <div className="resume-details">
            <Suspense fallback={<Loading />}>
              <Resume
                document={document}
                originalDocument={originalDocument}
                showHeader={false}
              />
            </Suspense>
          </div>
        );
      case 'Interview Questions':
        return (
          <div className="proposal-details">
            {this.renderleftBodyContent()}
            <div className="right-body-content">
              {this.renderProposalFeeback()}
              <Suspense fallback={<Loading />}>
                <InterviewQuestions
                  consultantName={proposalDetails.consultant_name}
                  proposalResourceId={proposalResourceId}
                  interviewQuestionsUrl={proposalDetails.interview_questions_url}
                  isConsultant={proposalDetails.consultant}
                  csrfToken={csrfToken}
                />
              </Suspense>
            </div>
          </div>
        );
      default:
        return (
          <div className="proposal-details">
            {this.renderleftBodyContent()}
            <div className="right-body-content">
              {proposalDetails.missing_required_response
                && (
                <div className="body-section">
                  <div className="missing-requirements">
                    <span>
                      <strong>{proposalDetails.missing_required_response.header}</strong>
                    </span>
                    <span>
                      Remember that the employer will choose up to 3
                      consultants to interview based on their proposals.
                    </span>
                    {Object.entries(proposalDetails.missing_required_response.requirements)
                      .map(([key, value]) => {
                        if (key.match(/_errors_count/) && value > 0) {
                          return (
                            <span className="body-text-3 my-3" key={key}>
                              {`${value} missing responses in ${key.replace(/_errors_count/, '').replace(/_/g, ' ').toLowerCase()}!`}
                            </span>
                          );
                        }
                        return null;
                      })}
                  </div>

                </div>
                )}
              { proposalViews
                && (
                <div className="body-section">
                  <UserViewsTable
                    userViews={proposalViews}
                    userHeader="Employer"
                  />
                </div>
                )}
              {this.renderProposalFeeback()}
              {showAdvisorRemark
                && (
                <div className="body-section">
                  {renderAdvisorRemark(proposalDetails.advisor_remark)}
                </div>
                )}

              {(proposalDetails.edit_requested || proposalDetails.rejection_reason
                || proposalDetails.withdraw_reason)
                && (
                <div className="body-section">
                  <div className="alert alert-warning">
                    { proposalDetails.rejection_reason
                      && (
                      <Fragment>
                        { proposalDetails.rejection_reason.user
                          && (
                          <span className="g-column rejection-reason mb-3">
                            {`User: ${proposalDetails.rejection_reason.user}`}
                          </span>
                          )}
                        <span className="g-column rejection-reason mb-2">
                          {proposalDetails.rejection_reason.decline_reason}
                        </span>
                        <span className="g-column rejection-reason mb-2">
                          {proposalDetails.rejection_reason.text_description}
                        </span>
                      </Fragment>
                      )}
                    { proposalDetails.withdraw_reason
                      && (
                      <Fragment>
                        { proposalDetails.withdraw_reason.user
                          && (
                          <span className="g-column rejection-reason mb-2">
                            {`User: ${proposalDetails.withdraw_reason.user}`}
                          </span>
                          )}
                        <span className="g-column rejection-reason mb-2">
                          {proposalDetails.withdraw_reason.withdraw_reason}
                        </span>
                        <span className="g-column rejection-reason mb-2">
                          {proposalDetails.withdraw_reason.text_description}
                        </span>
                      </Fragment>
                      )}
                    { proposalDetails.edit_requested
                      && (
                      <Fragment>
                        <blockquote className="mt-3 secondary-body-text">
                          {proposalDetails.edit_requested.comment}
                        </blockquote>
                        <span>
                          {`Edits requested on ${proposalDetails.edit_requested.created_at}`}
                        </span>
                      </Fragment>
                      )}
                  </div>
                </div>
                )}
              <div className="body-section">
                { proposalDetails.about_consultant
                && (
                <Fragment>
                  <h2 className="hearder-teal-green">About</h2>
                  <span>{proposalDetails.about_consultant}</span>
                  <hr />
                </Fragment>
                )}
                { proposalDetails.screening_question.questions.length
                  ? (
                    <div className="expertise-tags one">
                      <h2 className="hearder-teal-green">
                        {proposalDetails.screening_question.header}
                      </h2>
                      {proposalDetails.screening_question.questions.map((q) => (
                        <ProposalResponse
                          checked={q.checked}
                          description={q.description}
                          notes={q.notes}
                          extraSpanClasses={q.extraSpanClasses}
                        />
                      ))}
                      <hr />
                    </div>
                  )
                  : null}
                {(proposalDetails.therapeutic_areas || proposalDetails.indications)
                  && (
                  <div className="expertise-tags two">
                    <div className="row">
                      {proposalDetails.therapeutic_areas
                        && renderExpertiseTag('Therapeutic Areas', proposalDetails.therapeutic_areas, 'col-md-6')}
                      {proposalDetails.indications
                        && renderExpertiseTag('Indications', proposalDetails.indications, 'col-md-6')}
                    </div>
                    <hr />
                  </div>
                  )}
                {(proposalDetails.therapies || proposalDetails.regulatory_pathways)
                  && (
                  <div className="expertise-tags three">
                    <div className="row">
                      {proposalDetails.therapies
                        && renderExpertiseTag('Product Technologies', proposalDetails.therapies, 'col-md-6')}
                      {proposalDetails.regulatory_pathways
                        && renderExpertiseTag('Regulatory Pathways', proposalDetails.regulatory_pathways, 'col-md-6')}
                    </div>
                    <hr />
                  </div>
                  )}
                {(proposalDetails.education_levels || proposalDetails.accreditations)
                  && (
                  <div className="expertise-tags four">
                    <div className="row">
                      {proposalDetails.education_levels
                        && renderExpertiseTag('Education Requirements', proposalDetails.education_levels, 'col-md-6')}
                      {proposalDetails.accreditations
                        && renderExpertiseTag('Certifications', proposalDetails.accreditations, 'col-md-6')}
                    </div>
                    <hr />
                  </div>
                  )}
                {proposalDetails.languages
                  && (
                  <div className="expertise-tags">
                    <div className="row">
                      {proposalDetails.languages
                        && renderExpertiseTag('Languages', proposalDetails.languages)}
                    </div>
                    <hr />
                  </div>
                  )}
                {proposalDetails.consultant_question
                  && (
                  <div className="expertise-tags">
                    <div className="row">
                      <div className="col-xs-12 expertise-tag">
                        <h2 className="hearder-teal-green">Questions from the Consultant</h2>
                        <ProposalResponse
                          notes={proposalDetails.consultant_question}
                        />
                      </div>
                    </div>
                  </div>
                  )}
              </div>
            </div>
          </div>
        );
    }
  }

  render() {
    const {
      consultantDetails,
      proposalResourceId,
      csrfToken,
      initialTeamMembers,
      invitableRoles,
      editableRoles,
      basePermissionsUrl,
      copyLink,
      buttonEnabled,
      showNewProposalUX,
    } = this.props;
    const {
      isSticky, headerTabs, proposalDetails, showAddCoworkerModal, showWaitlistModal,
    } = this.state;
    return (
      <div className="new-proposal-ux">
        <div className={`sticky-proposal-header ${isSticky && 'show-sticky'}`}>
          <div className="sticky-proposal-header__nav">
            <div className="header-info">
              {this.renderNameAndStatus()}
            </div>
            {this.renderHeaderActions(true)}
          </div>
        </div>
        <div className="proposal-header__container">
          <div className="proposal-header__nav">
            <div className="proposal-header__top">
              <div className="header-info">
                {this.renderNameAndStatus()}
              </div>
              <div className="proposal-header__details">
                {consultantDetails.rating_avg
                  && (
                  <span className="proposal-header__rating">
                    <StarRatings
                      rating={consultantDetails.rating_avg}
                      starRatedColor="#EDA318"
                      starHoverColor="#EDA318"
                      starDimension="20px"
                      starSpacing="1px"
                      numberOfStars={5}
                      name="rating_avg"
                    />
                    <span className="ms-2">
                      {consultantDetails.rating_avg}
                    </span>
                  </span>
                  )}
                {(consultantDetails.city || consultantDetails.city_location)
                  && (
                  <span className="proposal-header__location">
                    <span className="">{imgTag(userMapIcon, 'location-icon')}</span>
                    {consultantDetails.city || consultantDetails.city_location}
                  </span>
                  )}
                { consultantDetails.years_of_experience
                  && (
                  <span className="proposal-header__experience">
                    <span className="">{imgTag(experienceIcon, 'experience-icon')}</span>
                    {consultantDetails.years_of_experience}
                  </span>
                  )}
                { proposalDetails.rep
                  && (
                  <Fragment>
                    <a
                      href={proposalDetails.consultant_profile_link}
                      className="proposal-header__link"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="">{imgTag(profileIcon, 'profile-icon')}</span>
                      View Profile
                    </a>
                    <a
                      href={consultantDetails.clorify_link}
                      className="proposal-header__link"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="">{imgTag(cloraIcon, 'clora-icon')}</span>
                      View Clorify
                    </a>
                  </Fragment>
                  )}
              </div>
            </div>
            {this.renderHeaderActions()}
            <div className="proposal-header__tabs">
              {headerTabs.map((tab, i) => this.renderTab(tab, i))}
            </div>
          </div>
          <div className="proposal-header__footer-note">
            <div className="footer-note__container">
              <div className="left-content">
                {proposalDetails.submitted_info}
                <span className="ms-2">{imgTag(clockIcon, 'clora-icon')}</span>
              </div>
              <div className="right-content">
                <span>
                  Project:
                  <a
                    className="project_link"
                    href={`${proposalDetails.project_link}?tabIndex=0`}
                  >
                    {proposalDetails.project_name}
                  </a>
                </span>
                {(proposalDetails.rep || proposalDetails.employer)
                  && (
                  <span className="coworker-modal">
                    <Suspense fallback={<Loading />}>
                      <AddCoworkersModal
                        csrfToken={csrfToken}
                        initialTeamMembers={initialTeamMembers}
                        invitableRoles={invitableRoles}
                        editableRoles={editableRoles}
                        basePermissionsUrl={basePermissionsUrl}
                        proposalResourceId={proposalResourceId}
                        copyLink={copyLink}
                        buttonEnabled={buttonEnabled}
                        consultantName={proposalDetails.consultant_name}
                        location="proposal"
                        showModalCallback={() => {
                          this.setState({ showAddCoworkerModal: false });
                        }}
                        modalIsOpen={showAddCoworkerModal}
                        showNewProposalUX={showNewProposalUX}
                      />
                    </Suspense>
                    <button
                      className="btn btn--text-blue"
                      onClick={this.handleShowCoworkerModal}
                    >
                      Add
                    </button>
                  </span>
                  )}
              </div>
            </div>
          </div>
        </div>
        <div className="proposal-body__container consultant-overview">
          {this.renderTabContent()}
        </div>
        <Suspense fallback={<Loading />}>
          <WaitlistedNotification
            consultantName={proposalDetails.consultant_name}
            showModal={showWaitlistModal}
            handleShowCoworkerModal={this.handleShowCoworkerModal}
            handleShowQuestion={this.handleShowAskQuestion}
            isEmployer={proposalDetails.employer}
            handleCloseModal={this.handleToggleWaitlistModal}
          />
        </Suspense>
      </div>
    );
  }
}

ProposalShow.propTypes = {
  consultantDetails: PropTypes.shape({}),
  proposalDetails: PropTypes.shape({}),
  csrfToken: PropTypes.string.isRequired,
  proposalResourceId: PropTypes.string.isRequired,
  initialTeamMembers: PropTypes.shape({ team_members: PropTypes.arrayOf(PropTypes.shape({})) }),
  invitableRoles: PropTypes.arrayOf(PropTypes.string),
  editableRoles: PropTypes.arrayOf(PropTypes.string),
  basePermissionsUrl: PropTypes.string.isRequired,
  copyLink: PropTypes.string.isRequired,
  buttonEnabled: PropTypes.bool,
  cloraCreaeRroposalCommentsUrl: PropTypes.string.isRequired,
  createInterviewUrl: PropTypes.string.isRequired,
  acceptProposalUrl: PropTypes.string.isRequired,
  editProposalUrl: PropTypes.string.isRequired,
  proposal: PropTypes.shape({}).isRequired,
  proposalPastRolesUrl: PropTypes.string.isRequired,
  pastRoles: PropTypes.arrayOf(PropTypes.shape({})),
  selectedPastRolesIds: PropTypes.arrayOf([]),
  selectedPastRoles: PropTypes.arrayOf(PropTypes.shape({})),
  proposalQuestionUrl: PropTypes.string.isRequired,
  completionPercentage: PropTypes.number,
  userPath: PropTypes.string.isRequired,
  consultantInterviewPath: PropTypes.string.isRequired,
  canCancelInterview: PropTypes.bool,
  canEditInterview: PropTypes.bool,
  proposalViews: PropTypes.arrayOf(PropTypes.shape({})),
  isSignedIn: PropTypes.bool,
  signIn: PropTypes.bool,
  currentUserEmail: PropTypes.string.isRequired,
  showAdvisorRemark: PropTypes.bool,
  animation: PropTypes.bool,
  updateUserSettingsUrl: PropTypes.string.isRequired,
  disabledSubmitProposal: PropTypes.bool,
  referralProps: PropTypes.shape({}),
  generateShareableLink: PropTypes.string.isRequired,
  showShareAction: PropTypes.bool,
  sourceOptions: PropTypes.arrayOf(PropTypes.shape({})),
  recipientOptions: PropTypes.arrayOf(PropTypes.shape({})),
  showNewProposalUX: PropTypes.bool,
  visibleFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  hiddenFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  consultantFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  projectsOptions: PropTypes.arrayOf(PropTypes.shape({})),
  reasonOptions: PropTypes.arrayOf(PropTypes.shape({})),
  withdrawUrl: PropTypes.string.isRequired,
  projectResourceId: PropTypes.string.isRequired,
  repOrOwner: PropTypes.bool,
  showBackgroundCheck: PropTypes.bool,
  newInterviewSlot: PropTypes.bool,
  submitToOpenMarketplace: PropTypes.bool,
  availableInterviewSlotUrl: PropTypes.string.isRequired,
};

ProposalShow.defaultProps = {
  consultantDetails: {},
  proposalDetails: {
    status: 'drafting',
  },
  reasonOptions: [],
  buttonEnabled: false,
  canCancelInterview: false,
  canEditInterview: false,
  proposalViews: [],
  isSignedIn: false,
  signIn: false,
  showAdvisorRemark: '',
  animation: false,
  submitToOpenMarketplace: false,
  disabledSubmitProposal: false,
  referralProps: {},
  showNewProposalUX: false,
  repOrOwner: false,
  showBackgroundCheck: false,
  newInterviewSlot: false,
};

export default ProposalShow;
